import { useCallback, useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  useImportStatementMutation,
  useParsePreviewMutation,
} from '../api/bankStatementManagementImportApi';
import type { BankStatementImportCurrencyOption, BankStatementImportPreview } from '../types';
import {
  buildImportRequest,
  groupKeyFor,
  groupReviewedTransactions,
  initializeReviewedTransactions,
  type ReviewedBankStatementTransaction,
  type ReviewedStatementGroup,
  type StatementOverrides,
} from '../reviewHelpers';
import { extractErrorMessage } from '../../../utils/errorUtils';
import { getBankStatementImportErrorRowId } from '../api/bankStatementImportError';
import { BankStatementSourceSummary } from './BankStatementSummaries';
import { StatementGroupEditor } from './StatementGroupEditor';
import { getImportDisabledReason } from '../validation';

const maxPreviewFileSizeBytes = 10 * 1024 * 1024;

type BankStatementManagementImportDialogProps = {
  open: boolean;
  title?: string | null;
  nodeKey: string;
  onClose: () => void;
  onImported?: (message?: string | null) => void;
};

const acceptedExtensions = '.csv,.xlsx';
const EMPTY_WARNINGS: string[] = [];

const updateRow = (
  rows: ReviewedBankStatementTransaction[],
  rowId: string,
  patch: Partial<ReviewedBankStatementTransaction>
) => rows.map((row) => (row.RowId === rowId ? { ...row, ...patch } : row));

export const BankStatementManagementImportDialog = ({
  open,
  title,
  nodeKey,
  onClose,
  onImported,
}: BankStatementManagementImportDialogProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BankStatementImportPreview | null>(null);
  const [reviewedTransactions, setReviewedTransactions] = useState<
    ReviewedBankStatementTransaction[]
  >([]);
  const [statementOverrides, setStatementOverrides] = useState<StatementOverrides>({});
  const [busyFileRows, setBusyFileRows] = useState<Set<string>>(new Set());
  const handleFileBusyChange = useCallback((rowId: string, busy: boolean) => {
    setBusyFileRows((current) => {
      if (current.has(rowId) === busy) return current;
      const next = new Set(current);
      if (busy) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  }, []);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [importFailure, setImportFailure] = useState<{
    message: string;
    rowId: string | null;
  } | null>(null);
  const [parsePreview, parseState] = useParsePreviewMutation();
  const [importStatement, importState] = useImportStatementMutation();
  const resetImportMutation = importState.reset;

  const errorMessage =
    importFailure?.message ??
    (importState.error
      ? extractErrorMessage(importState.error, 'Failed to import bank statement.')
      : parseState.error
        ? extractErrorMessage(parseState.error, 'Failed to parse bank statement.')
        : validationMessage);
  const failedTransactionRowId = importFailure?.rowId ?? null;

  const isBusy = parseState.isLoading || importState.isLoading || busyFileRows.size > 0;

  const selectedFileLabel = useMemo(() => {
    if (!selectedFile) return 'No file selected';
    return `${selectedFile.name} (${Math.max(1, Math.round(selectedFile.size / 1024))} KB)`;
  }, [selectedFile]);

  const previewWarnings = preview?.Warnings ?? EMPTY_WARNINGS;
  const statementGroups = useMemo(
    () => groupReviewedTransactions(reviewedTransactions, statementOverrides, preview),
    [preview, reviewedTransactions, statementOverrides]
  );
  const currencyOptions = useMemo(() => {
    const options = new Map<number, BankStatementImportCurrencyOption>();

    preview?.Lookups.Currencies.forEach((currency) => {
      options.set(currency.Id, currency);
    });

    reviewedTransactions.forEach((transaction) => {
      if (transaction.CrmCurrencyId && transaction.Currency) {
        options.set(transaction.CrmCurrencyId, {
          Id: transaction.CrmCurrencyId,
          Code: transaction.Currency,
          DisplayName: transaction.Currency,
        });
      }
    });

    if (preview?.Statement.CrmCurrencyId && preview.Statement.AccountCurrency) {
      options.set(preview.Statement.CrmCurrencyId, {
        Id: preview.Statement.CrmCurrencyId,
        Code: preview.Statement.AccountCurrency,
        DisplayName: preview.Statement.AccountCurrency,
      });
    }

    return Array.from(options.values()).sort((a, b) =>
      (a.DisplayName || a.Code).localeCompare(b.DisplayName || b.Code)
    );
  }, [preview, reviewedTransactions]);
  const categoryOptions = preview?.Lookups.Categories ?? [];
  const invoiceOptions = preview?.Lookups.Invoices ?? [];
  const counterpartyOptions = preview?.Lookups.Counterparties ?? [];
  const companyOptions = preview?.Lookups.Companies ?? [];
  const financeOptions = preview?.Lookups.FinanceAccounts ?? [];

  const resetReviewedState = () => {
    setPreview(null);
    setReviewedTransactions([]);
    setStatementOverrides({});
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    parseState.reset();
    importState.reset();
    setImportFailure(null);

    if (file && file.size > maxPreviewFileSizeBytes) {
      setSelectedFile(null);
      resetReviewedState();
      setValidationMessage('File size exceeds the maximum allowed limit of 10 MB.');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    resetReviewedState();
    setValidationMessage(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setValidationMessage(null);
    setImportFailure(null);
    importState.reset();
    parseState.reset();

    try {
      const result = await parsePreview({ nodeKey, file: selectedFile }).unwrap();
      setPreview(result);
      setReviewedTransactions(initializeReviewedTransactions(result));
      setStatementOverrides({});
    } catch {
      resetReviewedState();
    }
  };

  const handleClose = () => {
    if (isBusy) return;
    parseState.reset();
    importState.reset();
    setSelectedFile(null);
    resetReviewedState();
    setValidationMessage(null);
    setImportFailure(null);
    onClose();
  };

  const importDisabledReason = useMemo(
    () => getImportDisabledReason(!!preview, statementGroups, reviewedTransactions),
    [preview, reviewedTransactions, statementGroups]
  );

  const handleTransactionChange = useCallback(
    (rowId: string, patch: Partial<ReviewedBankStatementTransaction>) => {
      resetImportMutation();
      setImportFailure(null);
      setValidationMessage(null);
      setReviewedTransactions((current) => updateRow(current, rowId, patch));
    },
    [resetImportMutation]
  );

  const handleTransactionDelete = useCallback(
    (rowId: string) => {
      resetImportMutation();
      setImportFailure(null);
      setValidationMessage(null);
      setReviewedTransactions((current) => current.filter((row) => row.RowId !== rowId));
    },
    [resetImportMutation]
  );

  const handleStatementDelete = useCallback(
    (group: ReviewedStatementGroup) => {
      resetImportMutation();
      setImportFailure(null);
      setValidationMessage(null);
      const rowIds = new Set(group.transactions.map((row) => row.RowId));
      setReviewedTransactions((current) => current.filter((row) => !rowIds.has(row.RowId)));
      setStatementOverrides((current) => {
        if (!(group.key in current)) return current;
        const next = { ...current };
        delete next[group.key];
        return next;
      });
    },
    [resetImportMutation]
  );

  const handleStatementChange = useCallback(
    (group: ReviewedStatementGroup, patch: Partial<ReviewedStatementGroup>) => {
      resetImportMutation();
      setImportFailure(null);
      setValidationMessage(null);
      const rowPatch: Partial<ReviewedBankStatementTransaction> = {};
      if ('CrmRelationId' in patch) rowPatch.CrmRelationId = patch.CrmRelationId ?? null;
      if ('CompanyName' in patch) rowPatch.CompanyName = patch.CompanyName ?? null;
      if ('CrmRelationFinanceId' in patch) {
        rowPatch.CrmRelationFinanceId = patch.CrmRelationFinanceId ?? null;
        rowPatch.BankAccountName = patch.BankAccountName ?? null;
        rowPatch.BankName = patch.BankName ?? null;
      }
      if ('CrmCurrencyId' in patch) {
        rowPatch.CrmCurrencyId = patch.CrmCurrencyId ?? null;
        rowPatch.Currency = patch.AccountCurrency ?? null;
      }

      const nextKey = groupKeyFor({ ...group.transactions[0], ...rowPatch });
      const nextStatementDate = patch.statementDate ?? group.statementDate;
      setStatementOverrides((current) => {
        const next = { ...current };
        if (nextKey !== group.key) delete next[group.key];
        next[nextKey] = {
          statementDate: nextStatementDate,
          fromDate:
            patch.fromDate ??
            (patch.statementDate !== undefined && group.fromDate === group.statementDate
              ? nextStatementDate
              : group.fromDate),
          toDate:
            patch.toDate ??
            (patch.statementDate !== undefined && group.toDate === group.statementDate
              ? nextStatementDate
              : group.toDate),
          openingBalance:
            'openingBalance' in patch ? (patch.openingBalance ?? null) : group.openingBalance,
          closingBalance:
            'closingBalance' in patch ? (patch.closingBalance ?? null) : group.closingBalance,
        };
        return next;
      });
      setReviewedTransactions((current) =>
        current.map((row) =>
          group.transactions.some((item) => item.RowId === row.RowId)
            ? { ...row, ...rowPatch }
            : row
        )
      );
    },
    [resetImportMutation]
  );

  const handleImport = async () => {
    if (isBusy) return;
    if (importDisabledReason) {
      setValidationMessage(importDisabledReason);
      return;
    }

    if (!preview) return;

    setValidationMessage(null);
    setImportFailure(null);
    try {
      const result = await importStatement({
        nodeKey,
        request: buildImportRequest(preview, statementGroups),
      }).unwrap();
      parseState.reset();
      importState.reset();
      setSelectedFile(null);
      resetReviewedState();
      onImported?.(result.Message || 'Bank statement imported successfully.');
    } catch (error) {
      setImportFailure({
        message: extractErrorMessage(error, 'Failed to import bank statement.'),
        rowId: getBankStatementImportErrorRowId(error),
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { maxHeight: '92vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          {title || 'Import Bank Statement'}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={isBusy}
          edge="end"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto' }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={isBusy}
            >
              Choose file
              <input hidden type="file" accept={acceptedExtensions} onChange={handleFileChange} />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {selectedFileLabel}
            </Typography>
            <Button variant="contained" onClick={handleUpload} disabled={!selectedFile || isBusy}>
              {parseState.isLoading ? <CircularProgress size={20} /> : 'Parse Preview'}
            </Button>
          </Stack>

          {errorMessage && (
            <Alert severity="error" sx={{ py: 0.25 }}>
              {errorMessage}
            </Alert>
          )}
          {preview && (
            <>
              <Divider />
              <BankStatementSourceSummary preview={preview} />

              {importDisabledReason && (
                <Alert severity="warning" sx={{ py: 0.25 }}>
                  {importDisabledReason}
                </Alert>
              )}

              {previewWarnings.length > 0 && (
                <Alert severity="warning" sx={{ py: 0.25 }}>
                  <Stack spacing={0.5}>
                    {previewWarnings.map((warning, index) => (
                      <span key={`${index}-${warning}`}>{warning}</span>
                    ))}
                  </Stack>
                </Alert>
              )}

              <Stack spacing={2}>
                {statementGroups.map((group) => (
                  <StatementGroupEditor
                    key={group.key}
                    group={group}
                    failedTransactionRowId={failedTransactionRowId}
                    transactionErrorMessage={errorMessage}
                    invoiceOptions={invoiceOptions}
                    onFileBusyChange={handleFileBusyChange}
                    categoryOptions={categoryOptions}
                    counterpartyOptions={counterpartyOptions}
                    companyOptions={companyOptions}
                    financeOptions={financeOptions}
                    currencyOptions={currencyOptions}
                    disabled={isBusy}
                    onStatementChange={handleStatementChange}
                    onTransactionChange={handleTransactionChange}
                    onTransactionDelete={handleTransactionDelete}
                    onDelete={handleStatementDelete}
                  />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button onClick={handleClose} disabled={isBusy}>
          Close
        </Button>
        {preview && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={isBusy || !!importDisabledReason}
          >
            {importState.isLoading ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BankStatementManagementImportDialog;
